import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_URL } from '../config/api';

const DEFAULT_PREFERENCES = {
  dislikedIngredients: [],
  dietaryType: 'equilibrado',
  cuisineStyle: 'mixto',
  favorites: [],
  quickLunch: false,
  personCount: 1,
  planType: 'weekly',
  planDays: 7
};

const DAY_LABELS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const MEAL_TYPE_LABELS = {
  breakfast: 'Desayuno',
  lunch: 'Almuerzo',
  snack: 'Merienda',
  dinner: 'Cena'
};

function MenuPage({ userId, token }) {
  const [menu, setMenu] = useState(null);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [manualMealNames, setManualMealNames] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const fetchPreferences = useCallback(async () => {
    const saved = localStorage.getItem('userPreferences');
    if (saved) {
      try {
        setPreferences({ ...DEFAULT_PREFERENCES, ...JSON.parse(saved) });
      } catch (_) {
        setPreferences(DEFAULT_PREFERENCES);
      }
    }

    try {
      const res = await fetch(`${API_URL}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const backendPreferences = {
          ...DEFAULT_PREFERENCES,
          ...(data?.user?.preferences || {})
        };
        setPreferences(backendPreferences);
        localStorage.setItem('userPreferences', JSON.stringify(backendPreferences));
      }
    } catch (_) {
      // localStorage fallback
    }
  }, [userId, token]);

  const fetchMenu = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/menus/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMenu(data.menu);
      } else if (res.status === 404) {
        setError('No hay menú para esta semana.');
      } else {
        setError('Error al cargar menú');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
    setLoading(false);
  }, [userId, token]);

  useEffect(() => {
    fetchPreferences();
    fetchMenu();
  }, [fetchPreferences, fetchMenu]);

  const generateMenu = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/menus/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ preferences })
      });
      const data = await res.json();
      if (data.ok) {
        setMenu(data.menu);
        localStorage.setItem('menuId', data.menu._id);
        localStorage.removeItem('shoppingListId');
        setError('');
      } else {
        setError(data.error || 'Error al generar menú');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
    setLoading(false);
  };

  const completeMeal = async (day, mealType) => {
    if (!menu?._id) return;
    try {
      const res = await fetch(`${API_URL}/meals/${menu._id}/complete-meal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ day, mealType })
      });
      if (res.ok) {
        fetchMenu();
      } else {
        setError('Error al marcar comida');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const regenerateMeal = async (day, mealType, forcedMealName = '') => {
    if (!menu?._id) return;
    try {
      const res = await fetch(`${API_URL}/meal-changes/${menu._id}/regenerate-meal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          day,
          mealType,
          preferences,
          stock: {},
          forcedMealName
        })
      });
      const data = await res.json();
      if (data.ok) {
        fetchMenu();
        if (forcedMealName) {
          const key = `${day}-${mealType}`;
          setManualMealNames((prev) => ({ ...prev, [key]: '' }));
          const matchInfo = data.matchInfo;
          if (matchInfo?.matchedName) {
            setInfo(`Reemplazo aplicado: "${matchInfo.matchedName}" (confianza ${Math.round((matchInfo.confidence || 0) * 100)}%).`);
          } else if (matchInfo?.suggestions?.length) {
            setInfo(`No hubo match exacto para "${forcedMealName}". Sugerencias: ${matchInfo.suggestions.slice(0, 3).join(', ')}`);
          } else {
            setInfo(`Reemplazo aplicado usando "${forcedMealName}".`);
          }
        } else {
          setInfo('Comida regenerada.');
        }
      } else {
        setError(data.error || 'Error al regenerar');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const mealsByDay = useMemo(() => {
    const grouped = {};
    (menu?.meals || []).forEach((meal) => {
      const day = meal.day || 1;
      if (!grouped[day]) grouped[day] = [];
      grouped[day].push(meal);
    });

    Object.keys(grouped).forEach((day) => {
      grouped[day].sort((a, b) => {
        const order = ['breakfast', 'lunch', 'snack', 'dinner'];
        return order.indexOf(a.mealType) - order.indexOf(b.mealType);
      });
    });

    return grouped;
  }, [menu]);

  const totalDays = useMemo(() => {
    const fromMenu = Number(menu?.durationDays || 0);
    if (fromMenu > 0) return fromMenu;
    const maxMealDay = Math.max(0, ...(menu?.meals || []).map((m) => m.day || 0));
    return maxMealDay || 7;
  }, [menu]);

  const getDayTitle = (dayNumber) => {
    if (!menu?.startDate) {
      return `Día ${dayNumber}`;
    }
    const base = new Date(menu.startDate);
    base.setDate(base.getDate() + (dayNumber - 1));
    const label = base.toLocaleDateString('es-AR', { weekday: 'short', day: '2-digit', month: '2-digit' });
    const dayOfWeek = DAY_LABELS[(base.getDay() + 6) % 7] || '';
    return `${dayOfWeek} (${label})`;
  };

  return (
    <div className="page menu-page">
      <h2>Tu Menú Semanal</h2>
      {menu?.personCount ? <p className="menu-persons">Planificado para {menu.personCount} persona(s)</p> : null}
      {error && <p className="error">{error}</p>}
      {info && <p className="success">{info}</p>}

      <button onClick={generateMenu} disabled={loading} className="btn-primary">
        {loading ? 'Generando...' : 'Generar Menú'}
      </button>

      {menu && (
        <div className="menu-grid">
          {Array.from({ length: totalDays }).map((_, index) => {
            const dayNumber = index + 1;
            const dayMeals = mealsByDay[dayNumber] || [];
            return (
              <section key={dayNumber} className="day-column">
                <h3 className="day-title">{getDayTitle(dayNumber)}</h3>
                {dayMeals.length === 0 ? (
                  <p className="empty-day">Sin comidas para este día</p>
                ) : (
                  dayMeals.map((meal, idx) => (
                    <div key={`${dayNumber}-${meal.mealType}-${idx}`} className="meal-card">
                      <p className="meal-type">{MEAL_TYPE_LABELS[meal.mealType] || meal.mealType}</p>
                      <h4>{meal.recipeData?.title}</h4>
                      <p className="meal-meta">
                        <span>{meal.recipeData?.time || 'Tiempo no definido'}</span>
                        <span>{meal.recipeData?.difficulty || 'Dificultad no definida'}</span>
                      </p>
                      <p className="ingredients">
                        <strong>Ingredientes:</strong> {meal.recipeData?.ingredients?.length || 0}
                      </p>
                      {meal.recipeData?.ingredients?.length ? (
                        <details className="ingredient-details">
                          <summary>Ver ingredientes</summary>
                          <ul>
                            {meal.recipeData.ingredients.map((ing, i) => (
                              <li key={i}>
                                {ing.quantity} {ing.unit} {ing.name}
                              </li>
                            ))}
                          </ul>
                        </details>
                      ) : null}
                      {meal.recipeData?.steps?.length ? (
                        <details className="recipe-steps">
                          <summary>Ver receta</summary>
                          <ol>
                            {meal.recipeData.steps.map((step, i) => (
                              <li key={i}>{step}</li>
                            ))}
                          </ol>
                          {meal.recipeData?.tips?.length ? (
                            <div className="recipe-tips">
                              <strong>Tips:</strong>
                              <ul>
                                {meal.recipeData.tips.map((tip, i) => (
                                  <li key={i}>{tip}</li>
                                ))}
                              </ul>
                            </div>
                          ) : null}
                        </details>
                      ) : null}
                      <div className="meal-actions">
                        <button
                          onClick={() => completeMeal(meal.day, meal.mealType)}
                          className={meal.completed ? 'btn-completed' : 'btn-secondary'}
                          disabled={meal.completed}
                        >
                          {meal.completed ? 'Hecha' : 'Marcar como hecha'}
                        </button>
                        <button
                          onClick={() => regenerateMeal(meal.day, meal.mealType)}
                          className="btn-change"
                          disabled={meal.completed}
                        >
                          Cambiar
                        </button>
                      </div>
                      <div className="manual-meal-row">
                        <input
                          type="text"
                          placeholder="Escribir comida deseada (ej: Paella)"
                          value={manualMealNames[`${meal.day}-${meal.mealType}`] || ''}
                          onChange={(e) => setManualMealNames((prev) => ({
                            ...prev,
                            [`${meal.day}-${meal.mealType}`]: e.target.value
                          }))}
                          disabled={meal.completed}
                        />
                        <button
                          className="btn-secondary"
                          disabled={meal.completed || !(manualMealNames[`${meal.day}-${meal.mealType}`] || '').trim()}
                          onClick={() => regenerateMeal(
                            meal.day,
                            meal.mealType,
                            (manualMealNames[`${meal.day}-${meal.mealType}`] || '').trim()
                          )}
                        >
                          Reemplazar
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MenuPage;
